import { Module } from 'vuex'
import { Event, EventDatePollEntry } from '@/model/event'
import { RootState } from './root.store'
import { eventService } from '@/services/invite/eventService'
import Vue from 'vue'
import { EventFormData, EventPatchData } from '@/interfaces/event'
import { attendanceService } from '@/services/invite/attendanceService'
import moment from 'moment'
import { roleService } from '@/services/invite/roleService'
import { Attendance } from '@/model/attendance'
import { namespace } from 'vuex-class'
import EventUtils from '@/utils/eventUtils'

interface EventRecords {
  [index: string]: Event;
}

export interface EventsState {
  events: EventRecords,
  pastEventsLoaded: boolean,
  loading: boolean
}

const eventModule: Module<EventsState, RootState> = {
  namespaced: true,
  state: {
    events: {} as EventRecords,
    pastEventsLoaded: false,
    loading: false
  },
  getters: {
    getEvents (state: EventsState): Event[] {
      return EventUtils.sortValidEvents(Object.values(state.events).filter((x) => x.date && moment(x.date) >= moment()))
    },
    getPastEvents (state: EventsState): Event[] {
      return EventUtils.sortValidEvents(Object.values(state.events).filter((x) => x.date && moment(x.date) < moment())).reverse()
    },
    getPollEvents (state: EventsState): Event[] {
      return EventUtils.sortValidEvents(Object.values(state.events).filter((x) => !x.date))
    },
    getEventById: (state: EventsState) => (id: string): Event | null => {
      if (id in state.events) {
        return state.events[id]
      } else {
        return null
      }
    },
    isLoading (state: EventsState): boolean {
      return state.loading
    }
  },
  mutations: {
    setEvents (state: EventsState, events: Event[]) {
      const result: EventRecords = {}
      events.forEach(function (event) {
        Vue.set(state.events, event.id, event)
      })
    },
    setLoading (state: EventsState, loading: boolean) {
      state.loading = loading
    },
    saveEvent (state: EventsState, event: Event) {
      Vue.set(state.events, event.id, event)
      // Add attendees to user store.
    },
    deleteEvent (state: EventsState, eventId: string) {
      Vue.delete(state.events, eventId)
    },
    saveAttendances (state: EventsState, { eventId, attendances }: { eventId: string, attendances: Attendance[] }) {
      Vue.set(state.events[eventId], 'attendances', attendances)
    }
  },
  actions: {
    async fetchItems ({ commit, state }) {
      const notLoaded = (Object.keys(state.events).length === 0)
      commit('setLoading', notLoaded)
      const response = await eventService.getEvents()
      const events = response.data
      commit('setEvents', events)

      events.forEach(e => {
        commit('users/saveUsers', e.attendances.map(x => x.user), { root: true })
      })

      commit('setLoading', false)
    },
    async fetchItemsIfNeeded ({ state, dispatch }) {
      if (Object.keys(state.events).length === 0) {
        await dispatch('fetchItems')
      }
    },
    async fetchPastEvents ({ state, commit }) {
      if (!state.pastEventsLoaded) {
        const response = await eventService.getEvents(true)
        const events = response.data
        commit('setEvents', events)
      }
    },
    async getEvent ({ getters, dispatch }, eventId: string) {
      const event = getters.getEventById(eventId)
      if (event != null) {
        return event
      } else {
        return dispatch('fetchEvent', eventId)
      }
    },
    async fetchEvent ({ commit }, eventId: string) {
      const response = await eventService.getEvent(eventId)
      const event = response.data
      commit('saveEvent', event)
      commit('users/saveUsers', event.attendances.map(x => x.user), { root: true })
      return response.data
    },
    async fetchAttendances ({ commit }, eventId: string) {
      const response = await eventService.getAttendances(eventId)
      const attendances = response.data
      commit('saveAttendances', { eventId: eventId, attendances: attendances })
      return attendances
    },
    async createEvent ({ commit }, data: EventFormData): Promise<Event> {
      // Call API first.
      const response = await eventService.createEvent(data)
      commit('saveEvent', response.data)
      return response.data
    },
    async editEvent ({ commit }, { id, payload }: { id: string, payload: EventFormData }): Promise<Event> {
      // Call API first.
      const response = await eventService.editEvent(id, payload)
      commit('saveEvent', response.data)
      return response.data
    },
    async patchEvent ({ commit }, { id, payload }: { id: string, payload: EventPatchData }): Promise<Event> {
      // Call API first.
      const response = await eventService.patchEvent(id, payload)
      commit('saveEvent', response.data)
      return response.data
    },
    async deleteEvent ({ commit }, eventId: string) {
      const response = await eventService.deleteEvent(eventId)
      commit('saveEvent', response.data)
      return response.data
    },
    async addAttendees ({ commit }, { event, attendeesIds }: { event: Event, attendeesIds: string[] }) {
      const response = await attendanceService.addAttendees(event.id, attendeesIds)
      event.attendances.push(...response.data)
      commit('saveEvent', event)
    },
    async updateAttendanceStatus ({ commit }, {
      event,
      attendance,
      status
    }: { event: Event, attendance: Attendance, status: string }) {
      const response = await attendanceService.updateAttendance(attendance.id, status)
      event.attendances = response.data
      commit('saveEvent', event)
    },
    async updateRole ({ commit }, { event, role }) {
      const response = await roleService.updateRole(role.id, role.name, role.attendancesLimit)
      Object.assign(role, response.data)
      commit('saveEvent', event)
    },
    async voteForDate ({ commit, getters }, {
      event,
      datePollItem,
      status
    }: { event: Event, datePollItem: EventDatePollEntry, status: string }) {
      const response = await eventService.voteForDate(event.id, datePollItem.id, status)
      Object.assign(datePollItem, response.data)
      commit('saveEvent', event)
    },
    async confirmDate ({ commit, getters }, {
      event,
      datePollItem
    }: { event: Event, datePollItem: EventDatePollEntry }) {
      const response = await eventService.confirmDate(event.id, datePollItem.id)
      Object.assign(event, response.data)
      commit('saveEvent', event)
    }
  }
}

export const eventNamespace = namespace('event')

export default eventModule
